<script>
import Swal from 'sweetalert2';
import { InternalAuditChecklistService } from "@/services/InternalAuditChecklistService";
import ChecklistAnswerTree from "@/components/modules/ia/vessel/ChecklistAnswerTree.vue";
import {mapActions, mapGetters} from "vuex";
import {faBars, faRedo, faSpinner} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {AlertService} from "@/services/AlertService";
import {ref} from "vue";

export default {
  components: { ChecklistAnswerTree, FontAwesomeIcon },

  data() {
    return {
      showSidebar: true,
      activeCategory: null,
      checklist: [],
      originalChecklist: [],
      saving: false,
      isLoading: false,
      _scrollBound: false,
      scrollTopOffset: 0,
      params: {
        audit_type_id : '',
        audit_type : ''
      }
    }
  },

  methods: {
    ref,
    ...mapActions([
      'getInternalAudit',
    ]),
    async initialize() {
      this.params.audit_type_id =
        this.selectedAuditRecord[this.$route.params.type].id;

      this.params.audit_type =
        this.$route.params.type;

      await this.fetchChecklist();
    },

    disableScroll() {
      if (this.isLoading && this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollTop = this.scrollTopOffset;
      }
    },

    async refreshChecklist(){
      if (this.$refs.scrollContainer) {
        this.scrollTopOffset = this.$refs.scrollContainer.scrollTop;
      }

      if(this.saveStateHasChanges === true){
        const result = await Swal.fire({
          title: 'Discard Changes?',
          text: 'You have unsaved changes. Do you want to discard them?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Proceed',
          confirmButtonColor: '#20c997',
          cancelButtonText: 'Cancel',
          reverseButtons: true
        });

        if (result.isConfirmed) {
          this.isLoading = true;
          await this.fetchChecklist();
          this.isLoading = false;
        }
      }
      else{
        this.isLoading = true;
        await this.fetchChecklist();
        this.isLoading = false;
      }
    },

    scrollToCategory(id) {

      const el =
        document.getElementById(id);

      const container =
        this.$refs.scrollContainer;

      if (el && container) {

        this.activeCategory = id;

        container.scrollTo({
          top: el.offsetTop - 20,
          behavior: 'smooth'
        });
      }
    },

    handleScroll() {

      const container =
        this.$refs.scrollContainer;

      if (!container) return;

      const scrollTop =
        container.scrollTop;

      const sections =
        this.checklist.map(item => ({

          id:
            `category-${item.ia_type_checklist_id}`,

          el:
            document.getElementById(
              `category-${item.ia_type_checklist_id}`
            )
        }));

      // ⭐ SUMMARY SECTION
      sections.push({

        id:
          'star-rating-summary',

        el:
          document.getElementById(
            'star-rating-summary'
          )
      });

      let current =
        this.activeCategory;

      for (const section of sections) {

        if (!section.el) continue;

        const top =
          section.el.offsetTop - 200;

        if (scrollTop >= top) {

          current = section.id;
        }
      }

      // ✅ ONLY UPDATE IF CHANGED
      if (current !== this.activeCategory) {

        this.activeCategory = current;
      }
    },

    async fetchChecklist() {

      // ✅ PRESERVE CURRENT UI STATE
      const previousActive =
        this.activeCategory;

      const container =
        this.$refs.scrollContainer;

      const previousScrollTop =
        container?.scrollTop || 0;

      // ✅ FETCH DATA
      const response =
        await InternalAuditChecklistService
          .getAnswersChecklist(this.params);

      // ✅ UPDATE CHECKLIST
      this.checklist =
        response.data.data.checklist;

      // ✅ KEEP ORIGINAL SNAPSHOT
      this.originalChecklist =
        JSON.parse(
          JSON.stringify(this.checklist)
        );

      // ✅ WAIT FOR DOM
      this.$nextTick(() => {

        const container =
          this.$refs.scrollContainer;

        if (!container) return;

        // ✅ BIND ONLY ONCE
        if (!this._scrollBound) {

          container.addEventListener(
            'scroll',
            this.handleScroll
          );

          this._scrollBound = true;
        }

        // ✅ RESTORE SCROLL POSITION
        container.scrollTop =
          previousScrollTop;

        // ✅ RESTORE ACTIVE CATEGORY
        if (previousActive) {

          this.activeCategory =
            previousActive;

        } else if (this.checklist.length) {

          // ONLY FIRST LOAD
          this.activeCategory =
            `category-${this.checklist[0].ia_type_checklist_id}`;
        }

        // ✅ RECALCULATE AFTER DOM FULLY SETTLES
        requestAnimationFrame(() => {

          this.handleScroll();

        });
      });
    },

    // 🔹 SUBCATEGORY RATING
    computeRatingLocal(item) {

      const notCategoryItems =
        (item.sub_items || []).filter(
          x => x.is_category === 0
        );

      const xCount =
        notCategoryItems.filter(
          x => x.is_ok === 0
        ).length;

      const hasScore =
        notCategoryItems.some(
          x => x.answer_type === 'score'
        );

      // SCORE TYPE
      if (hasScore) {

        const totalScore =
          notCategoryItems.reduce((sum, x) => {

            return sum + Number(x.pt_now || 0);

          }, 0);

        return (
          notCategoryItems.length
            ? totalScore / notCategoryItems.length
            : 5
        );
      }

      // BOOLEAN TYPE
      const totalCategory =
        notCategoryItems.length > 0
          ? 1
          : 0;

      const rating =
        totalCategory > 0
          ? (
            ((5 * totalCategory) - xCount)
            / totalCategory
          )
          : 5;

      return Math.max(0, rating);
    },

// 🔹 MAIN CATEGORY STATS
    getMainCategoryStats(item) {

      const categoryItems =
        (item.sub_items || []).filter(
          x => x.is_category === 1
        );

      const notCategoryItems =
        (item.sub_items || []).filter(
          x => x.is_category === 0
        );

      const categoryCount =
        categoryItems.length;

      const notCategoryCount =
        notCategoryItems.length;

      // SAME BACKEND LOGIC
      const totalCategory =
        notCategoryCount > 0
          ? categoryCount + 1
          : categoryCount;

      // ROOT NCR COUNT
      const rootNcrCount =
        notCategoryItems.filter(
          x => x.is_ok === 0
        ).length;

      // CHILD CATEGORY NCR COUNT
      const childNcrCount =
        categoryItems.reduce((sum, cat) => {

          return (
            sum +
            this.getMainCategoryStats(cat).totalX
          );

        }, 0);

      const totalX =
        rootNcrCount + childNcrCount;

      // SCORE TYPE
      const hasScore =
        notCategoryItems.some(
          x => x.answer_type === 'score'
        );

      if (hasScore) {

        const totalScore =
          notCategoryItems.reduce((sum, x) => {

            return sum + Number(x.pt_now || 0);

          }, 0);

        return {
          totalX: 0,
          rating:
            notCategoryItems.length
              ? totalScore / notCategoryItems.length
              : 5
        };
      }

      // BOOLEAN TYPE
      const rating =
        totalCategory > 0
          ? (
            ((5 * totalCategory) - totalX)
            / totalCategory
          )
          : 5;

      return {
        totalX,
        rating: Math.max(0, rating)
      };
    },

    // 🔹 convert rating → % width
    getStarPercentage(rating) {

      const rounded =
        Math.round(rating * 2) / 2;

      return (rounded / 5) * 100;
    },

    getStarClasses(rating) {

      const stars = [];

      for (let i = 1; i <= 5; i++) {

        if (rating >= i) {
          stars.push('full');

        } else if (rating >= i - 0.5) {
          stars.push('half');

        } else {
          stars.push('empty');
        }
      }

      return stars;
    },

    handleUpdate(payload) {
      console.log('Updated:', payload);
    },

    // ✅ FLATTEN TREE
    flattenItems(items) {

      let result = [];

      items.forEach(item => {

        // ONLY LEAF ITEMS
        if (item.is_category === 0) {
          result.push(item);
        }

        // RECURSIVE
        if (item.sub_items?.length) {

          result = result.concat(
            this.flattenItems(item.sub_items)
          );
        }
      });

      return result;
    },

    // ✅ GET ONLY CHANGED ITEMS
    getChangedChecklistItems() {

      const currentItems =
        this.flattenItems(this.checklist);

      const originalItems =
        this.flattenItems(this.originalChecklist);

      const changedItems = [];

      currentItems.forEach(current => {
        current.ref = current.ref === '' ? null : current.ref ?? null;

        // ✅ ALWAYS INCLUDE NEW ITEMS
        if (current.is_new) {
          const changedItem = {
            // COMMON
            ia_type_checklist_id: null,
            audit_type_id: current.audit_type_id ?? null,
            parent_id: current.parent_id ?? null,
            answer_type: current.answer_type ?? 'boolean',
            is_ok: current.is_ok ?? null,
            item_to_be_checked: current.item_to_be_checked ?? null,
            ncr_type_description: current.ncr_type_description ?? null,
            ref: current.ref ?? null,
            answerable: current.answerable ?? 1
          };

          if (this.params.audit_type === 'sms') {
            Object.assign(changedItem, {
              // SMS
              ncr_type: current.ncr_type ?? null,
              max_pt: current.max_pt ?? null,
              pt_now: current.pt_now ?? null,
              risk_level: current.risk_level ?? null
            });
          } else if (this.params.audit_type === 'isps') {
            Object.assign(changedItem, {
              // ISPS
              isps: current.isps ?? null,
              ssp: current.ssp ?? null
            });
          } else if (this.params.audit_type === 'cyber') {
            Object.assign(changedItem, {
              // CYBER
              csp: current.csp ?? null
            });
          }

          changedItems.push(changedItem);

          return;
        }

        // ✅ FIND ORIGINAL
        const original =
          originalItems.find(
            x =>
              x.ia_type_checklist_id ===
              current.ia_type_checklist_id
          );

        // SAFETY
        if (!original) return;

        // ✅ CHECK IF "OTHER ITEM"
        const isOtherItem =
          current.audit_type_id !== null &&
          current.audit_type_id !== undefined;

        // ✅ DETECT CHANGES
        const hasChanges =
          // COMMON — SMS + ISPS + CYBER
          current.is_ok !== original.is_ok ||
          current.ncr_type_description !== original.ncr_type_description ||
          current.ref !== original.ref ||

          // ONLY OTHER ITEMS CAN EDIT LABEL
          (
            isOtherItem &&
            current.item_to_be_checked !==
            original.item_to_be_checked
          ) ||

          // ISPS ONLY
          (
            this.params.type === 'isps' &&
            (
              current.isps !== original.isps ||
              current.ssp !== original.ssp
            )
          ) ||

          // CYBER ONLY
          (
            this.params.type === 'cyber' &&
            (
              current.csp !== original.csp
            )
          ) ||

          // SMS ONLY
          (
            this.params.type === 'sms' &&
            (
              current.ncr_type !== original.ncr_type ||
              current.risk_level !== original.risk_level ||
              current.max_pt !== original.max_pt ||
              current.pt_now !== original.pt_now
            )
          );

        if (hasChanges) {
          const changedItem = {
            // COMMON
            ia_type_checklist_id: current.ia_type_checklist_id,
            audit_type_id: current.audit_type_id ?? null,
            parent_id: current.parent_id ?? null,
            answer_type: current.answer_type ?? 'boolean',
            is_ok: current.is_ok ?? null,
            item_to_be_checked: current.item_to_be_checked ?? null,
            ncr_type_description: current.ncr_type_description ?? null,
            ref: current.ref ?? null,
            answerable: current.answerable ?? 1
          };

          if (this.params.audit_type === 'sms') {
            Object.assign(changedItem, {
              // BOOLEAN
              ncr_type: current.ncr_type ?? null,

              // SCORE
              max_pt: current.max_pt ?? null,
              pt_now: current.pt_now ?? null,

              risk_level: current.risk_level ?? null
            });
          } else if (this.params.audit_type === 'isps') {
            Object.assign(changedItem, {
              isps: current.isps ?? null,
              ssp: current.ssp ?? null
            });
          } else if (this.params.audit_type === 'cyber') {
            Object.assign(changedItem, {
              csp: current.csp ?? null
            });
          }

          changedItems.push(changedItem);
        }
      });

      return changedItems;
    },

    validateChecklist(
      items,
      errors = [],
      parents = {
        main: null,
        sub: null
      }
    ) {

      items.forEach(item => {

        // ✅ TRACK CATEGORY HIERARCHY
        let currentParents = { ...parents };

        if (item.is_category === 1) {

          // MAIN CATEGORY
          if (!parents.main) {

            currentParents.main =
              item.item_to_be_checked;

          } else {

            // SUB CATEGORY
            currentParents.sub =
              item.item_to_be_checked;
          }
        }

        // ✅ ONLY LEAF ITEMS
        if (item.is_category === 0) {

          // ❗ BOOLEAN VALIDATION
          if (
            item.answer_type === 'boolean' &&
            item.is_ok === 0
          ) {

            const missingFields = [];

            if (this.params.audit_type === 'sms'){
              if (!item.ncr_type) {
                missingFields.push('NCR Type');
              }

              if (!item.ncr_type_description) {
                missingFields.push('Description');
              }

              if (!item.risk_level) {
                missingFields.push('Risk Level');
              }
            }

            if (missingFields.length) {

              errors.push({

                id:
                item.ia_type_checklist_id,

                main:
                currentParents.main,

                sub:
                currentParents.sub,

                item:
                item.item_to_be_checked,

                issue:
                  `Missing: ${missingFields.join(', ')}`
              });

              this.$set(
                item,
                'has_error',
                true
              );

            } else {

              this.$set(
                item,
                'has_error',
                false
              );
            }
          }

          // ❗ SCORE VALIDATION
          if (
            item.answer_type === 'score'
          ) {

            if (
              item.max_pt !== null &&
              item.pt_now !== null &&
              Number(item.pt_now) >
              Number(item.max_pt)
            ) {

              errors.push({

                id:
                item.ia_type_checklist_id,

                main:
                currentParents.main,

                sub:
                currentParents.sub,

                item:
                item.item_to_be_checked,

                issue:
                  'Score exceeds max points'
              });

              this.$set(
                item,
                'has_error',
                true
              );

            } else {

              this.$set(
                item,
                'has_error',
                false
              );
            }
          }
        }

        // ✅ RECURSIVE
        if (item.sub_items?.length) {

          this.validateChecklist(
            item.sub_items,
            errors,
            currentParents
          );
        }
      });

      return errors;
    },

    async saveChecklist() {

      // ✅ VALIDATE
      const errors =
        this.validateChecklist(
          this.checklist
        );

      // ✅ SHOW VALIDATION ERRORS
      if (errors.length) {

        // GROUP BY MAIN CATEGORY
        const groupedErrors =
          errors.reduce((groups, err) => {

            const main =
              err.main || 'Uncategorized';

            if (!groups[main]) {
              groups[main] = [];
            }

            groups[main].push(err);

            return groups;

          }, {});

        // BUILD HTML USING STRING CONCAT
        let html = '';

        html += '<div style="';
        html += 'text-align:left;';
        html += 'max-height:400px;';
        html += 'overflow:auto;';
        html += 'font-size:10pt;';
        html += '">';

        html += '<div class="mb-3 font-weight-bold" ';
        html += 'style="font-size:10pt;">';

        html += '<b style="color:red">';
        html += errors.length;
        html += '</b> item(s) require attention.';
        html += '</div>';

        // LOOP MAIN CATEGORIES
        Object.keys(groupedErrors).forEach(main => {

          html += '<div style="';
          html += 'margin-bottom:16px;';
          html += 'border:1px solid #e5e7eb;';
          html += 'border-radius:8px;';
          html += 'overflow:hidden;';
          html += '">';

          // MAIN HEADER
          html += '<div style="';
          html += 'background:#d3313c;';
          html += 'color:white;';
          html += 'padding:10px 12px;';
          html += 'font-weight:700;';
          html += 'font-size:11pt;';
          html += '">';
          html += main;
          html += '</div>';

          // BODY
          html += '<div style="padding:10px;">';

          groupedErrors[main].forEach((err, index) => {

            html += '<div style="';
            html += 'padding:10px;';
            html += 'margin-bottom:10px;';
            html += 'border:1px solid #eee;';
            html += 'border-radius:6px;';
            html += 'background:#fafafa;';
            html += '">';

            // ITEM
            html += '<div style="';
            html += 'font-weight:600;';
            html += 'color:#111827;';
            html += '">';
            html += (index + 1) + '. ';
            html += err.item;
            html += '</div>';

            // SUB CATEGORY
            if (err.sub) {

              html += '<div style="';
              html += 'margin-top:4px;';
              html += 'color:#6b7280;';
              html += 'font-size:9pt;';
              html += '">';

              html += '<b>Sub Category:</b> ';
              html += err.sub;

              html += '</div>';
            }

            // ISSUE
            html += '<div style="';
            html += 'margin-top:6px;';
            html += 'color:#b91c1c;';
            html += 'font-weight:600;';
            html += '">';

            html += err.issue;

            html += '</div>';

            html += '</div>';
          });

          html += '</div>';
          html += '</div>';
        });

        html += '</div>';

        Swal.fire({
          icon: 'warning',
          title: 'Fields Required!',
          confirmButtonColor: '#20c997',
          html
        });

        return;
      }

      // ✅ ONLY CHANGED ITEMS
      const changedItems =
        this.getChangedChecklistItems();

      // ✅ NOTHING CHANGED
      if (!changedItems.length) {

        Swal.fire({
          icon: 'info',
          title: 'No Changes',
          text: 'There are no changes to save.',
          confirmButtonColor: '#20c997',
          allowOutsideClick: false,
        });

        return;
      }

      // ✅ PAYLOAD
      const payload = {

        audit_type:
        this.params.audit_type,

        audit_type_id:
        this.params.audit_type_id,

        checklist_items:
        changedItems
      };

      // ✅ CONFIRM SAVE
      const confirm =
        await Swal.fire({

          title: 'SAVE CHANGES?',

          text:
            'THIS WILL UPDATE THE CHECKLIST',

          icon: 'question',

          showCancelButton: true,

          confirmButtonColor: '#20c997',
          cancelButtonText: 'CANCEL',
          confirmButtonText:
            'YES, SAVE IT'
        });

      if (!confirm.isConfirmed) {
        return;
      }

      this.saving = true;

      // ✅ LOADING
      Swal.fire({
        title: 'SAVING CHECKLIST...',
        text: 'PLEASE WAIT',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      try {

        // ✅ SAVE
        await InternalAuditChecklistService
          .saveChecklist(payload);

        // ✅ REFRESH
        await this.fetchChecklist();

        // ✅ SUCCESS
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text:
            'Checklist saved successfully',
          timer: 1500,
          showConfirmButton: false
        });

      } catch (e) {

        // ❌ ERROR
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text:
            e?.response?.data?.message ||
            'Failed to save checklist'
        });

      } finally {

        this.saving = false;
      }
    },

    async cancelChanges() {

      // ✅ NOTHING TO RESET
      if (!this.saveStateHasChanges) {
        return;
      }

      const confirm = await Swal.fire({
        title: 'DISCARD CHANGES?',
        text: 'ALL UNSAVED CHANGES WILL BE REVERTED.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'YES, DISCARD',
        cancelButtonText: 'KEEP EDITING',
        confirmButtonColor: '#d3313c'
      });

      if (!confirm.isConfirmed) {
        return;
      }

      // ✅ DEEP RESET
      this.checklist =
        JSON.parse(
          JSON.stringify(this.originalChecklist)
        );

      // ✅ CLEAR VALIDATION STATES
      this.clearValidationFlags(this.checklist);

      Swal.fire({
        icon: 'success',
        title: 'CHANGES DISCARDED',
        timer: 1200,
        showConfirmButton: false
      });
    },

    clearValidationFlags(items) {

      items.forEach(item => {

        // remove local validation state
        if (item.has_error !== undefined) {
          this.$delete(item, 'has_error');
        }

        // recursive
        if (item.sub_items?.length) {
          this.clearValidationFlags(item.sub_items);
        }
      });
    },

    async downloadBlankChecklist() {
      swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        title: 'DOWNLOADING BLANK TEMPLATE \nPLEASE WAIT...'
      });
      swal.showLoading();

      const date = new Date();

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      try {

        const response =
          await InternalAuditChecklistService
            .downloadBlankChecklist({

              ...this.params,

              vessel_id:
              this.$route.params?.vessel_id
            });

        const url =
          window.URL.createObjectURL(
            new Blob([response.data])
          );

        const link =
          document.createElement('a');

        link.href = url;

        link.setAttribute(
          'download',
          `INTERNAL_AUDIT_CHECKLIST_STAR_EVALUATION_${month}_${day}_${year}.xlsx`
        );

        document.body.appendChild(link);

        link.click();

        link.remove();
        swal.close();
      } catch (e) {

        Swal.fire({
          icon: 'error',
          title: 'Download Failed',
          text:
            e?.response?.data?.message ||
            'Unable to download template'
        });
      }
    },

    importChecklist() {
      this.$refs.import_aic.click();
    },

    async submitImportAIC() {
      const result = await Swal.fire({
        title: 'ARE YOU SURE?',
        text: 'IMPORTING THIS FILE WILL OVERWRITE OR UPDATE THE EXISTING TABLE DATA. PLEASE MAKE SURE THE UPLOADED FILE IS CORRECT BEFORE PROCEEDING.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'YES, IMPORT',
        confirmButtonColor: '#20c997',
        cancelButtonText: 'CANCEL',
        reverseButtons: true
      });

      if (!result.isConfirmed) {
        this.$refs.import_aic.value = '';
        return;
      }

      try {

        Swal.fire({
          title:
            'IMPORTING INTERNAL AUDIT CHECKLIST<br>PLEASE WAIT.....',

          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,

          didOpen: () => {
            Swal.showLoading();
          }
        });

        const file =
          this.$refs.import_aic.files[0];

        if (!file) {
          return;
        }

        let formData = new FormData();

        formData.append('file', file);

        formData.append(
          'vessel_id',
          this.$route.params?.vessel_id
        );

        Object.keys(this.params).forEach(key => {

          formData.append(
            key,
            this.params[key]
          );
        });

        this.$refs.import_aic.value = '';

        await InternalAuditChecklistService
          .submitImportAIC(formData);

        // ✅ REFRESH CHECKLIST
        await this.fetchChecklist();

        Swal.fire({
          icon: 'success',
          title: 'IMPORTED SUCCESSFULLY',
          timer: 1500,
          showConfirmButton: false
        });

      } catch (e) {

        // const errors =
        //   e?.response?.data?.errors
        //     ?.import_errors || [];
        //
        // Swal.fire({
        //   icon: 'error',
        //   title: 'Import Failed',
        //   html:
        //     errors.length
        //       ? errors.join('<br>')
        //       : 'Import failed'
        // });

        const rawErrors = e?.response?.data?.errors?.import_errors || [];

        const groupedErrors = {};

        rawErrors.forEach(err => {
          const match = err.match(/\[(.*?)\]\s*(.*?):\s*(.*)/);

          let main = 'General';
          let item = '';
          let issue = err;
          let sub = null;

          if (match) {
            main = match[1];     // Sheet name or category
            item = match[2];     // Row / Item label
            issue = match[3];    // Actual error message
          }

          if (!groupedErrors[main]) {
            groupedErrors[main] = {};
          }

          if (!groupedErrors[main][item]) {
            groupedErrors[main][item] = {
              item,
              sub,
              issues: []
            };
          }

          groupedErrors[main][item].issues.push(issue);
        });

        const totalItems = Object.values(groupedErrors)
          .reduce((sum, group) => sum + Object.keys(group).length, 0);

        let html = '';

        html += '<div style="text-align:left;max-height:400px;overflow:auto;font-size:10pt;">';

        html += '<div class="mb-3 font-weight-bold" style="font-size:10pt;">';
        html += '<b style="color:red">';
        html += totalItems;
        html += '</b> item(s) require attention.';
        html += '</div>';

        // LOOP MAIN CATEGORIES
        Object.keys(groupedErrors).forEach(main => {

          html += '<div style="margin-bottom:16px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">';

          // HEADER
          html += '<div style="background:#d3313c;color:white;padding:10px 12px;font-weight:700;font-size:11pt;">';
          html += main;
          html += '</div>';

          // BODY
          html += '<div style="padding:10px;">';

          Object.values(groupedErrors[main]).forEach((err, index) => {

            html += '<div style="padding:10px;margin-bottom:10px;border:1px solid #eee;border-radius:6px;background:#fafafa;">';

            html += '<div style="font-weight:600;color:#111827;">';
            html += (index + 1) + '. ' + err.item;
            html += '</div>';

            if (err.sub) {
              html += '<div style="margin-top:4px;color:#6b7280;font-size:9pt;">';
              html += '<b>Sub Category:</b> ' + err.sub;
              html += '</div>';
            }

            html += '<div style="margin-top:6px;color:#b91c1c;font-weight:600;">';
            err.issues.forEach(msg => {
              html += '<div style="color:#b91c1c;">' + msg + '</div>';
            });
            html += '</div>';

            html += '</div>';
          });

          html += '</div>';
          html += '</div>';
        });

        html += '</div>';

        Swal.fire({
          icon: 'error',
          title: 'IMPORT FAILED',
          confirmButtonColor: '#20c997',
          html: rawErrors.length ? html : 'IMPORT FAILED'
        });
      }
    },
    async downloadChecklistReport(){
      swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        title: 'DOWNLOADING INTERNAL AUDIT CHECKLIST REPORT \nPLEASE WAIT...'
      });
      swal.showLoading();

      const date = new Date();

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      try {
        const response = await InternalAuditChecklistService.downloadInternalAuditChecklistReport({
          ...this.params,
          vessel_id:
          this.$route.params?.vessel_id
        })

        const url =window.URL.createObjectURL(new Blob([response.data]));

        const link = document.createElement('a');

        link.href = url;

        link.setAttribute(
          'download',
          `INTERNAL_AUDIT_CHECKLIST_REPORT_${month}_${day}_${year}.pdf`
        );

        document.body.appendChild(link);
        link.click();
        link.remove();

        swal.close();
      } catch (e) {

        Swal.fire({
          icon: 'error',
          title: 'Download Failed',
          text:
            e?.response?.data?.message ||
            'Unable to download template'
        });
      }
    },
  },
  computed: {

    ...mapGetters([
      'selectedAuditRecord'
    ]),

    faBars() { return faBars },
    faSpinner() { return faSpinner },
    faRedo () { return faRedo },

    // ✅ AVERAGE SCORE
    averageScore() {
      if (!this.checklist.length) {
        return 5;
      }

      const total =
        this.checklist.reduce((sum, item) => {

          return (
            sum +
            this.getMainCategoryStats(item).rating
          );

        }, 0);

      return total / this.checklist.length;
    },

    // ✅ TOTAL DEVALUATION
    totalDevaluation() {
      return this.checklist.reduce((sum, item) => {

        return (
          sum +
          Number(item.devaluation || 0)
        );

      }, 0);
    },

    // ✅ FINAL SMS RESULT
    smsAssessmentResult() {

      const result =
        this.averageScore -
        this.totalDevaluation;

      return result < 0
        ? 0
        : result;
    },

    saveStateHasChanges(){
      const changedItems =
        this.getChangedChecklistItems();

      return changedItems.length > 0;
      // ✅ NOTHING CHANGED
    }
  },

  beforeDestroy() {

    const container =
      this.$refs.scrollContainer;

    if (container) {

      container.removeEventListener(
        'scroll',
        this.handleScroll
      );
    }
  },

  async created() {
    await this.initialize();

    await this.getInternalAudit({
      id : this.selectedAuditRecord[this.$route.params.type].id,
      type : this.$route.params.type
    });
  },

  async beforeRouteLeave(to, from, next) {

    // ✅ NOTHING CHANGED
    if (this.saveStateHasChanges) {
      if (await AlertService.cancelChanges() === true) {
        next();
      }
    }
    else{
      next();
    }
  }
};
</script>

<template>
  <div v-if="this.checklist.length === 0" class="container-fluid border p-3">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center">
            <font-awesome-icon :icon="faSpinner" style="font-size: 45pt;" class="fa-spin"></font-awesome-icon>
            <div class="mt-3">
              <h3 class="font-weight-bold font-italic text-uppercase">Loading...</h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="container-fluid border p-3" style="border-radius: 5px;" v-else>

    <!-- HEADER -->
    <div class="row mb-3">
      <div class="col-6">
        <div class="my-1 text-uppercase font-weight-bold e-text-red" style="font-size: 12pt">
          <span @click="showSidebar = !showSidebar" class="mr-1" style="cursor: pointer"><font-awesome-icon :icon="faBars" style="font-size: 12pt;"></font-awesome-icon></span> Internal Audit Checklist - Star Evaluation
        </div>
      </div>

      <div class="col-6 text-right">
        <!-- ✅ Save button with loading state -->
        <!-- CANCEL BUTTON -->
        <button
          v-if="saveStateHasChanges"
          class="e-btn e-btn-red ml-1"
          @click="cancelChanges"
          :disabled="saving"
        >
          Cancel Changes
        </button>

        <!-- SAVE BUTTON -->
        <button
          class="e-btn ml-1"
          @click="saveChecklist"
          :disabled="saveStateHasChanges === false || saving"
          :class="saveStateHasChanges === false
              ? 'e-btn-green-disabled'
              : 'e-btn-green'
          ">
          <span v-if="saving">
            <font-awesome-icon :icon="faSpinner" class="fa-spin"></font-awesome-icon>
            Saving...
          </span>

          <span v-else class="text-white">
            Save Changes
          </span>
        </button>

        <button class="e-btn e-btn-blue ml-1" @click="importChecklist">
          Import Excel
        </button>
        <input type="file" ref="import_aic" style="display:none"
               @change.prevent="submitImportAIC"
               accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">

        <div class="dropdown dropright d-inline-block ml-1">
          <button
            id="committeeReport"
            class="e-btn e-btn-blue-gray ml-1"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <font-awesome-icon class="mr-1 e-text-white" icon="download"/>
            Download
          </button>

          <div class="dropdown-menu" aria-labelledby="committeeReport">
            <button class="dropdown-item text-secondary font-weight-bolder" @click="downloadBlankChecklist" type="button">
              <font-awesome-icon icon="file-excel"/>
              Blank Template
            </button>

            <button class="dropdown-item text-secondary font-weight-bolder" @click="downloadChecklistReport" type="button">
              <font-awesome-icon icon="file-pdf"/>
              Internal Audit Checklist Report
            </button>
          </div>
        </div>
      </div>
    </div>

    <hr>

    <div class="row">
      <!-- SIDEBAR -->
      <div
        v-if="showSidebar"
        class="col-2 col-lg-2 mb-3 pr-1 "
      >
        <div class="nav-checklist-sidebar scrollable-content-nav">

          <div class="sidebar-title">
            <div class="row">
              <div class="col-6" style="font-size: 10pt">
                Checklist Navigation
              </div>
              <div class="col-6 d-flex align-items-center justify-content-end">
                <button class="e-btn" @click="refreshChecklist" :disabled="isLoading">
                  <img width="10" height="10" src="https://img.icons8.com/mini-stickers/25/rotate-right.png" alt="rotate-right" :class="{ 'spin-icon': isLoading }"/>
                </button>
              </div>
            </div>
          </div>

          <!-- MAIN CATEGORY LINKS -->
          <div
            v-for="main in checklist"
            :key="main.ia_type_checklist_id"
            class="text-uppercase"
            :class="[
              'sidebar-link',
              {
                'sidebar-link-active':
                  activeCategory === `category-${main.ia_type_checklist_id}`
              }
            ]"
            @click="scrollToCategory(`category-${main.ia_type_checklist_id}`)"
          >
            {{ main.item_to_be_checked_acronym }}
          </div>

          <!-- ⭐ STAR RATING SUMMARY LINK -->
          <div
            :class="[
              'sidebar-link',
              {
                'sidebar-link-active':
                  activeCategory === 'star-rating-summary'
              }
            ]"
            class="text-uppercase"
            @click="scrollToCategory('star-rating-summary')"
          >
            Star Rating Summary
          </div>

        </div>
      </div>
      <!-- CONTENT -->
      <div
        ref="scrollContainer"
        :class="[
          showSidebar ? 'col-10 col-lg-10' : 'col-12',
          { 'is-loading': isLoading }
        ]"
        class="scrollable-content d-flex flex-column gap-3 position-relative"
        @scroll="disableScroll"
      >
        <!-- 1. LOADING OVERLAY -->
        <div
          v-if="isLoading"
          class="loading-overlay"
          :style="{ top: `${scrollTopOffset}px` }"
          @wheel.prevent
          @touchmove.prevent
        >
          <div class="loading-content">
            <img
              class="custom-spinner-icon"
              src="https://img.icons8.com/mini-stickers/100/rotate-right.png"
              alt="Loading..."
            />
            <span class="loading-text mt-2">Loading...</span>
          </div>
        </div>

        <!-- 2. CONTENT CONTAINER (Blurred while loading) -->
        <div class="d-flex flex-column gap-3" :class="{ 'content-loading-blur': isLoading }">
        <!-- TREE -->
        <div
          v-for="data in checklist"
          :key="data.ia_type_checklist_id"
          :id="`category-${data.ia_type_checklist_id}`"
        >
          <ChecklistAnswerTree
            :item="data"
            :level="0"
            @update="handleUpdate"
          />
        </div>

        <!-- ⭐ STAR RATING SUMMARY -->
        <div class="card rating-card mt-3" id="star-rating-summary">

          <div class="card-header rating-header">
            Star Rating Summary
          </div>

          <div class="card-body p-0">

            <table
              class="table table-bordered table-sm mb-0 rating-summary-table"
            >

              <thead>
              <tr>

                <th style="width:50px;">
                  #
                </th>

                <th>
                  Category
                </th>

                <th style="width:90px;">
                  Weight
                </th>

                <th style="width:100px;">
                  NCR Count
                </th>

                <th style="width:110px;">
                  Devaluation
                </th>

                <th style="width:140px;">
                  Rating
                </th>

                <th style="width:90px;">
                  Score
                </th>

              </tr>
              </thead>

              <tbody>

              <!-- CATEGORY ROWS -->
              <tr
                v-for="(main, index) in checklist"
                :key="main.ia_type_checklist_id"
              >

                <!-- NUMBER -->
                <td class="text-center font-weight-bold">
                  {{ index + 1 }}
                </td>

                <!-- CATEGORY -->
                <td>
                  {{ main.item_to_be_checked }}
                </td>

                <!-- WEIGHT -->
                <td class="text-center">
                  {{ Number(main.weight || 0).toFixed(2) }}
                </td>

                <!-- NCR COUNT -->
                <td class="text-center text-danger font-weight-bold">
                  {{ getMainCategoryStats(main).totalX }}
                </td>

                <!-- DEVALUATION -->
                <td class="text-center text-danger font-weight-bold">
                  {{ (Number(main.weight || 0).toFixed(2) * getMainCategoryStats(main).totalX).toFixed(2)}}
                </td>

                <!-- STARS -->
                <td>

                  <div
                    class="rating-stars modern justify-content-center"
                  >

                  <span
                    v-for="(star, i) in getStarClasses(
                      getMainCategoryStats(main).rating
                    )"
                    :key="i"
                    class="star"
                    :class="star"
                  >
                    ★
                  </span>

                  </div>

                </td>

                <!-- SCORE -->
                <td class="text-center">

                <span class="rating-pill">
                  {{ getMainCategoryStats(main).rating.toFixed(2) }}
                </span>

                </td>

              </tr>

              <tr>
                <td colspan="7" class="bg-dark"></td>
              </tr>

              <!-- AVERAGE SCORE -->
              <tr class="summary-row average-row">

                <td colspan="6"
                    class="text-right font-weight-bold">
                  Average Score
                </td>

                <td class="text-center font-weight-bold">
                  {{ averageScore.toFixed(2) }}
                </td>

              </tr>

              <!-- TOTAL DEVALUATION -->
              <tr class="summary-row devaluation-row">

                <td colspan="6"
                    class="text-right font-weight-bold">
                  Devaluation
                </td>

                <td
                  class="text-center font-weight-bold text-danger"
                >
                  {{ totalDevaluation.toFixed(2) }}
                </td>

              </tr>

              <!-- SMS RESULT -->
              <tr class="summary-row sms-row">

                <td colspan="6"
                    class="text-right font-weight-bold">
                  SMS Assessment Result
                </td>

                <td class="text-center font-weight-bold">

                <span class="sms-result-pill">
                  {{ smsAssessmentResult.toFixed(2) }}
                </span>

                </td>

              </tr>

              </tbody>

            </table>

            <!-- NOTE -->
            <div class="assessment-note">

              Note:
              Result equal or less than 2,
              Verification Audit or Remote Audit
              in 3 Months is required

            </div>

          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* =========================================================
   LAYOUT & SCROLL CONTAINERS
========================================================= */

.scrollable-content {
  position: relative;
  height: 75vh;
  overflow-y: scroll;
  scrollbar-gutter: stable;
  padding-bottom: 0;
}

.scrollable-content.is-loading {
  overflow-y: hidden !important;
}

.scrollable-content-nav {
  height: 75vh;
  overflow-y: auto;
  padding-bottom: 0;
}

/* =========================================================
   LOADING OVERLAY & BLUR EFFECTS
========================================================= */

.loading-overlay {
  position: absolute;
  left: 0;
  width: 100%;
  height: 75vh;

  display: flex;
  justify-content: center;
  align-items: center;

  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 999;
  border-radius: 12px;
  pointer-events: auto;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.content-loading-blur {
  filter: blur(4px);
  pointer-events: none;
  user-select: none;
}

.custom-spinner-icon {
  width: 50px;
  height: 50px;
  animation: spin-icon 1s linear infinite;
  display: block;
}

.spin-icon {
  animation: spin-icon 1s linear infinite;
  display: inline-block; /* Required for transforms/rotations on inline elements */
}

.loading-text {
  font-size: 15px;
  font-weight: 600;
  color: #4b5563;
}

/* Rotation Keyframes */
@keyframes spin-icon {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* =========================================================
   ⭐ STAR RATING SUMMARY
========================================================= */

.rating-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.08);
  background: #ffffff;
}

.rating-header {
  background: linear-gradient(
    135deg,
    #d3313c,
    #b91c1c
  );
  color: white;
  font-weight: 700;
  font-size: 14px;
  padding: 12px 16px;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
}

/* =========================================================
   TABLE
========================================================= */

.rating-summary-table {
  margin-bottom: 0;
}

.rating-summary-table th {
  background: #f3f4f6;
  text-align: center;
  vertical-align: middle;
  font-size: 11px;
  font-weight: 700;
  color: #374151;
  border-color: #d1d5db;
}

.rating-summary-table td {
  vertical-align: middle;
  font-size: 12px;
  border-color: #e5e7eb;
}

/* Hover */
.rating-summary-table tbody tr:hover {
  background: #f9fafb;
}

/* =========================================================
   STARS
========================================================= */

.rating-stars.modern {
  display: flex;
  gap: 3px;
}

.star {
  font-size: 18px;
  position: relative;
  color: #e5e7eb;
}

/* FULL */
.star.full {
  color: #fbbf24;
  text-shadow: 0 0 5px rgba(251,191,36,0.55);
}

/* HALF */
.star.half {
  color: #e5e7eb;
}

.star.half::before {
  content: '★';
  position: absolute;
  left: 0;
  top: 0;
  width: 50%;
  overflow: hidden;
  color: #fbbf24;
  text-shadow: 0 0 5px rgba(251,191,36,0.55);
}

/* EMPTY */
.star.empty {
  color: #d1d5db;
}

/* =========================================================
   SCORE PILL
========================================================= */

.rating-pill {
  background: linear-gradient(
    135deg,
    #fbbf24,
    #f59e0b
  );
  color: white;
  font-weight: 700;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 999px;
  min-width: 55px;
  display: inline-block;
  text-align: center;
  box-shadow: 0 2px 6px rgba(245,158,11,0.3);
}

/* =========================================================
   SUMMARY ROWS
========================================================= */

.summary-row td {
  font-size: 13px;
  padding-top: 10px;
  padding-bottom: 10px;
}

/* Average */
.average-row {
  background: #fef3c7;
}

/* Devaluation */
.devaluation-row {
  background: #fee2e2;
}

/* SMS Result */
.sms-row {
  background: #dbeafe;
}

/* =========================================================
   SMS RESULT PILL
========================================================= */

.sms-result-pill {
  background: linear-gradient(
    135deg,
    #0284c7,
    #0369a1
  );
  color: white;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  display: inline-block;
  box-shadow: 0 2px 8px rgba(2,132,199,0.25);
}

/* =========================================================
   NOTE
========================================================= */

.assessment-note {
  padding: 12px 16px;
  font-size: 11px;
  font-style: italic;
  color: #7f1d1d;
  background: #fef2f2;
  border-top: 1px solid #fecaca;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
}

/* =========================================================
   SIDEBAR
========================================================= */

.nav-checklist-sidebar {
  position: sticky;
  top: 10px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0 0 12px 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  max-height: 85vh;
  overflow-y: auto;
  overflow-x: hidden;
}

/* TITLE */
.sidebar-title {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #ffffff;
  padding: 12px 12px 8px 12px;
  font-size: 11pt;
  font-weight: 700;
  margin-bottom: 10px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

/* LINK */
.sidebar-link {
  margin: 0 12px 6px 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 10pt;
  font-weight: 500;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

/* HOVER */
.sidebar-link:hover {
  background: #d3313c;
  color: white;
  transform: translateX(3px);
}

/* ACTIVE */
.sidebar-link-active {
  background: #d3313c;
  color: white;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(211, 49, 60, 0.35);
}
</style>
